// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useCallback, useEffect } from 'react';
import { getConfigSettings } from '@api/portalServices';
import type { AuthenticationStrengthGetResponse, Setting } from '@api/types';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useAuthenticationStrengthQuery } from '@portal/containers/security/useAuthenticationStrength';
import {
  computeNewSettingValues,
  useUpdateConfigSettings,
} from '@portal/containers/security/useConfigSettings';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountRecoveryEnabledSettingValues,
  DiscardEnrollmentPhotosSettingValues,
  type FormValues,
  getSettingsTemplate,
  InstanceKey,
  SettingsValues,
  UserCanShowLiveFeedSettingValues,
} from '@portal/containers/security/constants';

export function findMfaTypeSetting(settings?: Setting[]): Setting | undefined {
  return settings?.find(item => item.name === SettingsValues.name);
}

export function findUserCanShowLiveFeedSetting(settings?: Setting[]): Setting | undefined {
  return settings?.find(item => item.name === UserCanShowLiveFeedSettingValues.name);
}

export function findDiscardEnrollmentPhotosSetting(settings?: Setting[]): Setting | undefined {
  return settings?.find(item => item.name === DiscardEnrollmentPhotosSettingValues.name);
}

export function findAccountRecoveryEnabledSetting(settings?: Setting[]): Setting | undefined {
  return settings?.find(item => item.name === AccountRecoveryEnabledSettingValues.name);
}

const useEmitOnError = (error: ApiError | null) => {
  const { emitError } = useErrorContext();
  useEffect(() => {
    if (error) {
      emitError(new AppError(error));
    }
  }, [error, emitError]);
};

export const useSecurityData = () => {
  const queryClient = useQueryClient();

  const {
    data: ussAuthnStrength,
    isFetching: isFetchingUssAuthnStrength,
    error: ussAuthnStrengthError,
  } = useAuthenticationStrengthQuery(InstanceKey.USS);
  const {
    data: iamAuthnStrength,
    isFetching: isFetchingIamAuthnStrength,
    error: iamAuthnStrengthError,
  } = useAuthenticationStrengthQuery(InstanceKey.IAM);

  const {
    data: ussSetting,
    isFetching: isFetchingUSS,
    error: ussError,
    refetch: refetchUSS,
  } = useQuery<Setting | undefined, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, InstanceKey.USS],
    queryFn: async () => {
      const response = await getConfigSettings({
        ...getSettingsTemplate,
        instanceKey: InstanceKey.USS,
      });
      return findMfaTypeSetting(response?.settings);
    },
    retry: false,
    staleTime: 0,
  });

  const {
    data: iamSetting,
    isFetching: isFetchingIAM,
    error: iamError,
    refetch: refetchIAM,
  } = useQuery<Setting | undefined, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, InstanceKey.IAM],
    queryFn: async () => {
      const response = await getConfigSettings({
        ...getSettingsTemplate,
        instanceKey: InstanceKey.IAM,
      });
      return findMfaTypeSetting(response?.settings);
    },
    retry: false,
    staleTime: 0,
  });

  // The AM Portal response carries every portal-admin setting, so both switches
  // are derived from one read instead of one request per setting.
  const {
    data: amPortalSettings,
    isFetching: isFetchingAmPortalSettings,
    error: amPortalSettingsError,
    refetch: refetchAmPortalSettings,
  } = useQuery<Setting[], ApiError>({
    queryKey: [QueryKey.AM_PORTAL_CONFIG_SETTINGS],
    queryFn: async () => {
      const response = await getConfigSettings({
        ...getSettingsTemplate,
        instanceKey: InstanceKey.AM_PORTAL,
      });
      return response?.settings ?? [];
    },
    retry: false,
    staleTime: 0,
  });

  const userCanShowLiveFeedSetting = findUserCanShowLiveFeedSetting(amPortalSettings);

  const {
    data: discardEnrollmentPhotosSetting,
    isFetching: isFetchingDiscardEnrollmentPhotos,
    error: discardEnrollmentPhotosError,
    refetch: refetchDiscardEnrollmentPhotos,
  } = useQuery<Setting | null, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, DiscardEnrollmentPhotosSettingValues.name],
    queryFn: async () => {
      const response = await getConfigSettings(getSettingsTemplate);
      return findDiscardEnrollmentPhotosSetting(response?.settings) ?? null;
    },
    retry: false,
    staleTime: 0,
  });

  const {
    data: accountRecoveryEnabledSetting,
    isFetching: isFetchingAccountRecoveryEnabled,
    error: accountRecoveryEnabledError,
    refetch: refetchAccountRecoveryEnabled,
  } = useQuery<Setting | undefined, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, AccountRecoveryEnabledSettingValues.name],
    queryFn: async () => {
      // account-recovery-enabled is queried without an instanceKey.
      const response = await getConfigSettings(getSettingsTemplate);
      return findAccountRecoveryEnabledSetting(response?.settings);
    },
    retry: false,
    staleTime: 0,
  });

  const { mutate: updateSettings, isPending: isUpdating } = useUpdateConfigSettings();

  useEmitOnError(ussError);
  useEmitOnError(iamError);
  useEmitOnError(ussAuthnStrengthError);
  useEmitOnError(iamAuthnStrengthError);
  useEmitOnError(amPortalSettingsError);
  useEmitOnError(discardEnrollmentPhotosError);
  useEmitOnError(accountRecoveryEnabledError);

  const refetchAll = useCallback(async () => {
    await Promise.all([
      refetchUSS(),
      refetchIAM(),
      refetchAmPortalSettings(),
      refetchDiscardEnrollmentPhotos(),
      refetchAccountRecoveryEnabled(),
    ]);
  }, [refetchUSS, refetchIAM, refetchAmPortalSettings, refetchDiscardEnrollmentPhotos, refetchAccountRecoveryEnabled]);

  // Sync the authentication-strength caches with the values just saved.
  // Trusts the BFF to store the sent value verbatim (no normalization).
  const syncAuthnStrengthCache = useCallback(
    (formValues: FormValues) => {
      const { ussNewSettingValue, iamNewSettingValue } = computeNewSettingValues(formValues);
      queryClient.setQueryData<AuthenticationStrengthGetResponse | undefined>(
        [QueryKey.AUTHENTICATION_STRENGTH, InstanceKey.USS],
        prev => (prev ? { ...prev, currentValue: ussNewSettingValue } : prev),
      );
      queryClient.setQueryData<AuthenticationStrengthGetResponse | undefined>(
        [QueryKey.AUTHENTICATION_STRENGTH, InstanceKey.IAM],
        prev => (prev ? { ...prev, currentValue: iamNewSettingValue } : prev),
      );
    },
    [queryClient],
  );

  const isLoading =
    isFetchingUSS ||
    isFetchingIAM ||
    isFetchingUssAuthnStrength ||
    isFetchingIamAuthnStrength ||
    isFetchingAmPortalSettings ||
    isFetchingDiscardEnrollmentPhotos ||
    isFetchingAccountRecoveryEnabled ||
    isUpdating;

  return {
    ussAuthnStrength,
    iamAuthnStrength,
    ussAuthnStrengthError,
    iamAuthnStrengthError,
    ussSetting,
    iamSetting,
    userCanShowLiveFeedSetting,
    discardEnrollmentPhotosSetting: discardEnrollmentPhotosSetting ?? undefined,
    accountRecoveryEnabledSetting,
    isLoading,
    refetchAll,
    syncAuthnStrengthCache,
    updateSettings,
  };
};
